import { SegmentKind } from "@/services/backend/vbs/treatments/types/segmentKind";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Certificate } from "@/services/backend/vbs/certificates/types/certificate";

const vbsCertificatesApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsCertificateCreate: build.mutation<Certificate, CreateRequest>({
      query: (request) => ({
        url: "/vbs/certificate",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "VbsCertificate", id: "LIST" }],
    }),
    vbsCertificateShow: build.query<Certificate, ShowRequest>({
      query: (request) => ({
        url: "/vbs/certificate",
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        "VbsCertificate",
      ],
    }),
    vbsCertificateList: build.query<Lst<Certificate>, ListRequest>({
      query: (request) => ({
        url: "/vbs/certificate/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsCertificate"),
    }),
    vbsCertificateUpdateIssueDate: build.mutation<
      Certificate,
      UpdateIssueDateRequest
    >({
      query: (request) => ({
        url: `/vbs/certificate/issue-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        { type: "VbsCertificate", id: "LIST" },
      ],
    }),
    vbsCertificateUpdateWorkStartedAt: build.mutation<
      Certificate,
      UpdateWorkStartedAtRequest
    >({
      query: (request) => ({
        url: "/vbs/certificate/work-started-at",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        { type: "VbsCertificate", id: "LIST" },
      ],
    }),
    vbsCertificateUpdateWorkEndedAt: build.mutation<
      Certificate,
      UpdateWorkEndedAtRequest
    >({
      query: (request) => ({
        url: "/vbs/certificate/work-ended-at",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        { type: "VbsCertificate", id: "LIST" },
      ],
    }),
    vbsCertificateUpdatePostalAddress: build.mutation<
      Certificate,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/certificate/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsCertificate", id: request.id },
        "VbsCertificate",
      ],
    }),
    vbsCertificateUpdateServiceAddress: build.mutation<
      Certificate,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/certificate/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsCertificate", id: request.id },
        "VbsCertificate",
      ],
    }),
    vbsCertificateUpdateSegments: build.mutation<
      Certificate,
      UpdateSegmentsRequest
    >({
      query: (request) => ({
        url: "/vbs/certificate/segments",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        { type: "VbsCertificate", id: "LIST" },
      ],
    }),
    vbsCertificatePdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      query: (request) => ({
        url: "/vbs/certificate",
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
      ],
    }),
    vbsCertificatePdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: "/vbs/certificate",
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    vbsCertificateSetImmutable: build.mutation<
      Certificate,
      SetImmutableRequest
    >({
      query: (request) => ({
        url: `/vbs/certificate/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsCertificate", id: request.id },
        "VbsCertificate",
        "NumbersDocumentNumber",
      ],
    }),
    vbsCertificateDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: "/vbs/certificate",
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCertificate", id: request.id },
        { type: "VbsCertificate", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useVbsCertificateCreateMutation,
  useVbsCertificateShowQuery,
  useVbsCertificateListQuery,
  useVbsCertificateUpdateIssueDateMutation,
  useVbsCertificateUpdateWorkStartedAtMutation,
  useVbsCertificateUpdateWorkEndedAtMutation,
  useVbsCertificateUpdatePostalAddressMutation,
  useVbsCertificateUpdateServiceAddressMutation,
  useVbsCertificateUpdateSegmentsMutation,
  useVbsCertificateSetImmutableMutation,
  useVbsCertificatePdfPreviewQuery,
  useVbsCertificatePdfDownloadMutation,
  useVbsCertificateDeleteMutation,
} = vbsCertificatesApi;

export interface ShowRequest {
  id: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface UpdateIssueDateRequest {
  id: string;
  issueDate: string;
}

export interface UpdateWorkStartedAtRequest {
  id: string;
  workStartedAt: string;
}

export interface UpdateWorkEndedAtRequest {
  id: string;
  workEndedAt: string;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateSegmentsRequest {
  id: string;
  segments: SegmentKind[];
}

export interface PDFRequest {
  id: string;
}

export interface SetImmutableRequest {
  id: string;
  setBy: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  issueDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}
